import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
} from "@mui/material";
import { Button } from "@platform/design-system";
import { useState } from "react";

import { useCopyTextToClipboard } from "../../../../hooks/useCopyTextToClipboard";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import {
  CarrierContactType,
  CarrierWithContactsByType,
} from "../../../../types/carrier";

const TOAST_AUTO_HIDE_DURATION = 2000;

// Roles listed in the same order as the Carriers card body, only ones with emails render.
const ROLE_ORDER: CarrierContactType[] = ["sales_rep", "support", "general"];

const ROLE_LABELS: Record<CarrierContactType, string> = {
  sales_rep: "Sales",
  support: "Support",
  general: "General",
};

export interface CarrierGroup {
  title: string;
  carriers: CarrierWithContactsByType[];
}

interface ContactRow {
  id: string;
  email: string;
  label: string;
}

interface CarrierRows {
  key: string;
  carrierName: string;
  rows: ContactRow[];
}

interface GroupRows {
  title: string;
  carriers: CarrierRows[];
}

const buildGroupRows = (groups: CarrierGroup[]): GroupRows[] =>
  groups
    .map((group, groupIndex) => ({
      title: group.title,
      carriers: group.carriers
        .map((carrier) => {
          const carrierKey = String(carrier.carrierId ?? carrier.carrierName);
          return {
            key: carrierKey,
            carrierName: carrier.carrierName,
            rows: ROLE_ORDER.flatMap((type) =>
              carrier.contactsByType[type].map((email) => ({
                id: `${groupIndex}-${carrierKey}-${type}-${email}`,
                email,
                label: `${ROLE_LABELS[type]}: ${email}`,
              })),
            ),
          };
        })
        .filter((carrier) => carrier.rows.length > 0),
    }))
    .filter((group) => group.carriers.length > 0);

const CopyAllContactsButton = ({
  groups,
}: {
  groups: CarrierGroup[];
}): React.JSX.Element | null => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { createToastNotification } = useToastNotificationService();
  const copyToClipboard = useCopyTextToClipboard({
    autoHideDuration: TOAST_AUTO_HIDE_DURATION,
  });

  const groupRows = buildGroupRows(groups);
  const allRows = groupRows.flatMap((group) =>
    group.carriers.flatMap((carrier) => carrier.rows),
  );

  const someSelected = selectedIds.size > 0;
  const allSelected =
    allRows.length > 0 && allRows.every((row) => selectedIds.has(row.id));
  const indeterminate = someSelected && !allSelected;

  if (allRows.length === 0) {
    return null;
  }

  const handleClose = (): void => {
    setAnchorEl(null);
    setSelectedIds(new Set());
  };

  const toggleRow = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = (): void => {
    setSelectedIds(
      allSelected ? new Set() : new Set(allRows.map((row) => row.id)),
    );
  };

  const handleCopy = (): void => {
    const emails = Array.from(
      new Set(
        allRows
          .filter((row) => selectedIds.has(row.id))
          .map((row) => row.email),
      ),
    );

    if (emails.length === 0) {
      createToastNotification({
        message: "No contacts selected.",
        severity: "warning",
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      });
      return;
    }

    // Semicolon-separated so the list pastes straight into an Outlook "To" line.
    copyToClipboard(
      emails.join("; "),
      "Contacts copied!",
      "Failed to copy contacts.",
    );
    handleClose();
  };

  return (
    <>
      <Button
        variant="secondary"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<ContentCopyIcon fontSize="small" />}
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? "copy-all-contacts-popover" : undefined}
        data-testid="copy-all-contacts-button"
      >
        COPY ALL CONTACTS
      </Button>
      <Popover
        id="copy-all-contacts-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 340,
              maxHeight: 520,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
            },
          },
        }}
        data-testid="copy-all-contacts-popover"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: 520,
            py: 1,
          }}
        >
          <MenuList disablePadding sx={{ overflowY: "auto" }}>
            <MenuItem
              onClick={toggleAll}
              data-testid="copy-all-contacts-select-all"
              sx={{ py: 1, px: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Checkbox
                size="small"
                disableRipple
                tabIndex={-1}
                checked={allSelected}
                indeterminate={indeterminate}
                sx={{ p: 0, mr: 1.5 }}
              />
              <ListItemText primary="Copy all Contacts" />
            </MenuItem>
            {groupRows.flatMap((group) => [
              <MenuItem
                key={`header-${group.title}`}
                disabled
                sx={{
                  py: 1,
                  px: 2,
                  minHeight: "auto",
                  borderBottom: 1,
                  borderColor: "divider",
                  "&.Mui-disabled": { opacity: 1 },
                }}
              >
                <ListItemText
                  primary={group.title}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: "fontWeightBold",
                        color: "text.primary",
                      },
                    },
                  }}
                />
              </MenuItem>,
              ...group.carriers.flatMap((carrier) => [
                <MenuItem
                  key={`carrier-${group.title}-${carrier.key}`}
                  disabled
                  sx={{
                    pt: 1,
                    pb: 0.5,
                    px: 2,
                    minHeight: "auto",
                    "&.Mui-disabled": { opacity: 1 },
                  }}
                >
                  <ListItemText
                    primary={carrier.carrierName}
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight: "fontWeightMedium",
                          color: "text.primary",
                        },
                      },
                    }}
                  />
                </MenuItem>,
                ...carrier.rows.map((row) => (
                  <MenuItem
                    key={row.id}
                    onClick={() => toggleRow(row.id)}
                    data-testid={`copy-contact-checkbox-${row.id}`}
                    sx={{
                      py: 1,
                      px: 2,
                      alignItems: "flex-start",
                      whiteSpace: "normal",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Checkbox
                      size="small"
                      disableRipple
                      tabIndex={-1}
                      checked={selectedIds.has(row.id)}
                      sx={{ p: 0, mr: 1.5, mt: 0.25 }}
                    />
                    <ListItemText
                      primary={row.label}
                      slotProps={{
                        primary: {
                          sx: { wordBreak: "break-word" },
                        },
                      }}
                    />
                  </MenuItem>
                )),
              ]),
            ])}
          </MenuList>
          <Box
            sx={{
              p: 1,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="primary"
              startIcon={<ContentCopyIcon fontSize="small" />}
              disabled={!someSelected}
              onClick={handleCopy}
              data-testid="copy-selected-contacts-button"
            >
              COPY SELECTED CONTACTS
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export { CopyAllContactsButton };
